import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BackButton } from '../components/ui/back-button';
import { Dices, Users, Clock, Plus, Edit, Trash2, Search, Filter, BookOpen, X, Monitor, Package, ExternalLink, Globe, Gamepad2, Link } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { gamesAPI } from '../services/api';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const GAME_THEMES = [
  // Fantasy & Médiéval
  { value: 'Médiéval-Fantastique', label: 'Médiéval-Fantastique', category: 'Fantasy' },
  { value: 'High Fantasy', label: 'High Fantasy', category: 'Fantasy' },
  { value: 'Dark Fantasy', label: 'Dark Fantasy', category: 'Fantasy' },
  { value: 'Heroic Fantasy', label: 'Heroic Fantasy', category: 'Fantasy' },
  { value: 'Mythologie', label: 'Mythologie', category: 'Fantasy' },
  
  // Science-Fiction
  { value: 'Science-Fiction', label: 'Science-Fiction', category: 'SF' },
  { value: 'Space Opera', label: 'Space Opera', category: 'SF' },
  { value: 'Cyberpunk', label: 'Cyberpunk', category: 'SF' },
  { value: 'Post-Apocalyptique', label: 'Post-Apocalyptique', category: 'SF' },
  { value: 'Dystopie', label: 'Dystopie', category: 'SF' },
  { value: 'Mecha', label: 'Mecha / Robots', category: 'SF' },
  
  // Horreur & Sombre
  { value: 'Horreur', label: 'Horreur', category: 'Sombre' },
  { value: 'Lovecraftien', label: 'Lovecraftien / Cosmique', category: 'Sombre' },
  { value: 'Gothique', label: 'Gothique', category: 'Sombre' },
  { value: 'Survival Horror', label: 'Survival Horror', category: 'Sombre' },
  { value: 'Vampires', label: 'Vampires', category: 'Sombre' },
  { value: 'Zombies', label: 'Zombies', category: 'Sombre' },
  
  // Historique & Réaliste
  { value: 'Contemporain', label: 'Contemporain', category: 'Réaliste' },
  { value: 'Historique', label: 'Historique', category: 'Réaliste' },
  { value: 'Guerre', label: 'Guerre / Militaire', category: 'Réaliste' },
  { value: 'Western', label: 'Western', category: 'Réaliste' },
  { value: 'Espionnage', label: 'Espionnage', category: 'Réaliste' },
  { value: 'Polar', label: 'Polar / Noir', category: 'Réaliste' },
  
  // Aventure & Action
  { value: 'Aventure', label: 'Aventure', category: 'Action' },
  { value: 'Pirates', label: 'Pirates', category: 'Action' },
  { value: 'Exploration', label: 'Exploration', category: 'Action' },
  { value: 'Super-Héros', label: 'Super-Héros', category: 'Action' },
  { value: 'Pulp', label: 'Pulp', category: 'Action' },
  
  // Spéciaux
  { value: 'Steampunk', label: 'Steampunk', category: 'Spécial' },
  { value: 'Dieselpunk', label: 'Dieselpunk', category: 'Spécial' },
  { value: 'Wuxia', label: 'Wuxia / Arts Martiaux', category: 'Spécial' },
  { value: 'Japon Féodal', label: 'Japon Féodal', category: 'Spécial' },
  { value: 'Enquête', label: 'Enquête / Mystère', category: 'Spécial' },
  { value: 'Humour', label: 'Humour / Décalé', category: 'Spécial' },
  { value: 'Anime', label: 'Anime / Manga', category: 'Spécial' },
  { value: 'Autre', label: 'Autre', category: 'Autre' }
];

const THEME_CATEGORIES = ['Fantasy', 'SF', 'Sombre', 'Réaliste', 'Action', 'Spécial', 'Autre'];

const GAME_FORMATS = [
  { value: 'Physique', label: 'Physique', icon: Package },
  { value: 'Numérique', label: 'Numérique', icon: Monitor }
];

const GameLibrary = () => {
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [themeFilter, setThemeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null); // For viewing game details
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    min_players: 2,
    max_players: 6,
    duration: '2-4h',
    themes: ['Médiéval-Fantastique'],
    format: ['Physique'],
    digital_link: '',
    website_url: '',
    image_url: ''
  });
  const { isAdmin, isMember, user } = useAuth();

  useEffect(() => {
    loadGames();
  }, []);

  useEffect(() => {
    filterGames();
  }, [games, searchQuery, themeFilter]);

  const loadGames = async () => {
    try {
      setLoading(true);
      const data = await gamesAPI.getAll();
      setGames(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des jeux');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterGames = () => {
    let filtered = [...games];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(game => 
        game.name.toLowerCase().includes(query) ||
        game.description.toLowerCase().includes(query)
      );
    }
    
    if (themeFilter !== 'all') {
      filtered = filtered.filter(game => {
        const gameThemes = game.themes || [game.theme]; // Support both old and new format
        return gameThemes.includes(themeFilter);
      });
    }
    
    setFilteredGames(filtered);
  };

  const getImageUrl = (url) => url || '';

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  const handleCreateGame = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await gamesAPI.create(formData);
      toast.success('Jeu ajouté à la bibliothèque !');
      setIsDialogOpen(false);
      resetForm();
      await loadGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateGame = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await gamesAPI.update(editingGame, formData);
      toast.success('Jeu mis à jour !');
      setIsDialogOpen(false);
      setEditingGame(null);
      resetForm();
      await loadGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGame = async (gameId, gameName) => {
    if (!window.confirm(`Supprimer "${gameName}" de la bibliothèque ?`)) return;
    
    try {
      await gamesAPI.delete(gameId);
      toast.success('Jeu supprimé');
      loadGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditDialog = (game) => {
    setEditingGame(game.game_id);
    setFormData({
      name: game.name,
      description: game.description,
      min_players: game.min_players,
      max_players: game.max_players,
      duration: game.duration,
      themes: game.themes || [game.theme || game.category || 'Médiéval-Fantastique'],
      format: game.format || ['Physique'],
      digital_link: game.digital_link || '',
      website_url: game.website_url || '',
      image_url: game.image_url || ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      min_players: 2,
      max_players: 6,
      duration: '2-4h',
      themes: ['Médiéval-Fantastique'],
      format: ['Physique'],
      digital_link: '',
      website_url: '',
      image_url: ''
    });
    setEditingGame(null);
  };

  const toggleFormat = (formatValue) => {
    setFormData(prev => {
      const currentFormats = prev.format || [];
      if (currentFormats.includes(formatValue)) {
        // Remove if already selected (but keep at least one)
        const newFormats = currentFormats.filter(f => f !== formatValue);
        return { ...prev, format: newFormats.length > 0 ? newFormats : currentFormats };
      } else {
        // Add if not selected
        return { ...prev, format: [...currentFormats, formatValue] };
      }
    });
  };

  const toggleTheme = (themeValue) => {
    setFormData(prev => {
      const currentThemes = prev.themes || [];
      if (currentThemes.includes(themeValue)) {
        // Remove if already selected (but keep at least one)
        const newThemes = currentThemes.filter(t => t !== themeValue);
        return { ...prev, themes: newThemes.length > 0 ? newThemes : currentThemes };
      } else {
        // Add if not selected
        return { ...prev, themes: [...currentThemes, themeValue] };
      }
    });
  };

  const getThemeColor = (theme) => {
    const colors = {
      // Fantasy
      'Médiéval-Fantastique': 'bg-purple-600/80 border-purple-400/50',
      'High Fantasy': 'bg-purple-500/80 border-purple-300/50',
      'Dark Fantasy': 'bg-purple-900/80 border-purple-600/50',
      'Heroic Fantasy': 'bg-violet-600/80 border-violet-400/50',
      'Mythologie': 'bg-indigo-600/80 border-indigo-400/50',
      // SF
      'Science-Fiction': 'bg-blue-600/80 border-blue-400/50',
      'Space Opera': 'bg-blue-500/80 border-blue-300/50',
      'Cyberpunk': 'bg-cyan-600/80 border-cyan-400/50',
      'Post-Apocalyptique': 'bg-orange-600/80 border-orange-400/50',
      'Dystopie': 'bg-slate-600/80 border-slate-400/50',
      'Mecha': 'bg-sky-600/80 border-sky-400/50',
      // Sombre
      'Horreur': 'bg-red-600/80 border-red-400/50',
      'Lovecraftien': 'bg-emerald-900/80 border-emerald-600/50',
      'Gothique': 'bg-stone-700/80 border-stone-500/50',
      'Survival Horror': 'bg-red-800/80 border-red-600/50',
      'Vampires': 'bg-rose-900/80 border-rose-600/50',
      'Zombies': 'bg-lime-800/80 border-lime-600/50',
      // Réaliste
      'Contemporain': 'bg-gray-600/80 border-gray-400/50',
      'Historique': 'bg-yellow-600/80 border-yellow-400/50',
      'Guerre': 'bg-zinc-600/80 border-zinc-400/50',
      'Western': 'bg-amber-700/80 border-amber-500/50',
      'Espionnage': 'bg-neutral-600/80 border-neutral-400/50',
      'Polar': 'bg-slate-700/80 border-slate-500/50',
      // Action
      'Aventure': 'bg-green-600/80 border-green-400/50',
      'Pirates': 'bg-teal-600/80 border-teal-400/50',
      'Exploration': 'bg-emerald-600/80 border-emerald-400/50',
      'Super-Héros': 'bg-pink-600/80 border-pink-400/50',
      'Pulp': 'bg-orange-500/80 border-orange-300/50',
      // Spécial
      'Steampunk': 'bg-amber-600/80 border-amber-400/50',
      'Dieselpunk': 'bg-stone-600/80 border-stone-400/50',
      'Wuxia': 'bg-red-500/80 border-red-300/50',
      'Japon Féodal': 'bg-rose-600/80 border-rose-400/50',
      'Enquête': 'bg-yellow-600/80 border-yellow-400/50',
      'Humour': 'bg-fuchsia-600/80 border-fuchsia-400/50',
      'Anime': 'bg-pink-500/80 border-pink-300/50',
      'Enquête': 'bg-indigo-600/80 border-indigo-400/50',
      'Humour': 'bg-green-600/80 border-green-400/50',
      'Autre': 'bg-slate-600/80 border-slate-400/50'
    };
    return colors[theme] || colors['Autre'];
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 pb-12">
      <SEO title="Ludothèque" description="La ludothèque de Just Rôle It : tous les jeux de rôle disponibles à l'association, par thème et format." path="/games" />
      <div className="container mx-auto max-w-7xl">
        <BackButton />
        
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2 tracking-wide">
              Bibliothèque de Jeux
            </h1>
            <p className="text-gray-400">
              {games.length} jeu{games.length !== 1 ? 'x' : ''} disponible{games.length !== 1 ? 's' : ''} à l'association
            </p>
          </div>
          
          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button">
                  <Plus className="mr-2 h-5 w-5" />
                  Ajouter un jeu
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-purple-300">
                    {editingGame ? 'Modifier le jeu' : 'Ajouter un jeu'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={editingGame ? handleUpdateGame : handleCreateGame} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-purple-300">Nom du jeu</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      placeholder="Ex: Donjons & Dragons 5e"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-purple-300">Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white min-h-[100px]"
                      placeholder="Décrivez le jeu..."
                      required
                    />
                  </div>
                  
                  {/* Image URL */}
                  <div className="space-y-2">
                    <Label className="text-purple-300">URL de l'image du jeu</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                        <Input
                          value={formData.image_url}
                          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                          className="bg-black/50 border-purple-500/30 text-white pl-10"
                          placeholder="https://exemple.com/image.jpg"
                        />
                      </div>
                      {formData.image_url && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          onClick={removeImage}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {formData.image_url && (
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-32 object-cover rounded-lg border border-purple-500/30 mt-2"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <p className="text-gray-500 text-xs">Collez l'URL d'une image hébergée en ligne</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-300">Joueurs min</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.min_players}
                        onChange={(e) => setFormData({ ...formData, min_players: parseInt(e.target.value) })}
                        className="bg-black/50 border-purple-500/30 text-white"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-300">Joueurs max</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.max_players}
                        onChange={(e) => setFormData({ ...formData, max_players: parseInt(e.target.value) })}
                        className="bg-black/50 border-purple-500/30 text-white"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-300">Durée</Label>
                      <Select value={formData.duration} onValueChange={(v) => setFormData({ ...formData, duration: v })}>
                        <SelectTrigger className="bg-black/50 border-purple-500/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-black border-purple-500/30">
                          <SelectItem value="30min" className="text-purple-300">30 minutes</SelectItem>
                          <SelectItem value="1h" className="text-purple-300">1 heure</SelectItem>
                          <SelectItem value="1-2h" className="text-purple-300">1-2 heures</SelectItem>
                          <SelectItem value="2-4h" className="text-purple-300">2-4 heures</SelectItem>
                          <SelectItem value="4h+" className="text-purple-300">4+ heures</SelectItem>
                          <SelectItem value="Campagne" className="text-purple-300">Campagne</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-300">Thèmes (cliquez pour sélectionner)</Label>
                      <div className="bg-black/50 border border-purple-500/30 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                        {THEME_CATEGORIES.map((category) => (
                          <div key={category} className="mb-3">
                            <p className="text-xs text-gray-500 uppercase mb-1">{category}</p>
                            <div className="flex flex-wrap gap-1">
                              {GAME_THEMES.filter(t => t.category === category).map((theme) => {
                                const isSelected = formData.themes?.includes(theme.value);
                                return (
                                  <button
                                    key={theme.value}
                                    type="button"
                                    onClick={() => toggleTheme(theme.value)}
                                    className={`px-2 py-1 rounded text-xs transition-all ${
                                      isSelected 
                                        ? `${getThemeColor(theme.value)} text-white` 
                                        : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
                                    }`}
                                  >
                                    {theme.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      {formData.themes?.length > 0 && (
                        <p className="text-xs text-purple-400">
                          Sélectionnés: {formData.themes.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Format (Physique / Numérique) */}
                  <div className="space-y-2">
                    <Label className="text-purple-300">Format du jeu</Label>
                    <div className="flex gap-4">
                      {GAME_FORMATS.map((format) => {
                        const Icon = format.icon;
                        const isSelected = formData.format?.includes(format.value);
                        return (
                          <button
                            key={format.value}
                            type="button"
                            onClick={() => toggleFormat(format.value)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                              isSelected 
                                ? 'border-purple-500 bg-purple-900/50 text-purple-300' 
                                : 'border-purple-500/30 bg-black/50 text-gray-400 hover:border-purple-500/50'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            {format.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Digital Link (shown only if Numérique is selected) */}
                  {formData.format?.includes('Numérique') && (
                    <div className="space-y-2">
                      <Label className="text-purple-300 flex items-center">
                        <Gamepad2 className="h-4 w-4 mr-2" />
                        Lien du format Numérique
                      </Label>
                      <Input
                        value={formData.digital_link}
                        onChange={(e) => setFormData({ ...formData, digital_link: e.target.value })}
                        className="bg-black/50 border-purple-500/30 text-white"
                        placeholder="Ex: https://roll20.net/... ou https://foundry..."
                      />
                    </div>
                  )}
                  
                  {/* Website URL */}
                  <div className="space-y-2">
                    <Label className="text-purple-300 flex items-center">
                      <Globe className="h-4 w-4 mr-2" />
                      Site web du jeu / éditeur
                    </Label>
                    <Input
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      className="bg-black/50 border-purple-500/30 text-white"
                      placeholder="Ex: https://www.wizards.com/dnd"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 neon-button disabled:opacity-50"
                  >
                    {submitting ? 'En cours...' : (editingGame ? 'Modifier' : 'Ajouter')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-purple-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un jeu..."
              className="pl-10 bg-black/50 border-purple-500/30 text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-purple-400" />
            <Select value={themeFilter} onValueChange={setThemeFilter}>
              <SelectTrigger className="w-[200px] bg-black/50 border-purple-500/30 text-purple-300">
                <SelectValue placeholder="Thème" />
              </SelectTrigger>
              <SelectContent className="bg-black border-purple-500/30 max-h-[300px]">
                <SelectItem value="all" className="text-purple-300">Tous les thèmes</SelectItem>
                {GAME_THEMES.map((theme) => (
                  <SelectItem key={theme.value} value={theme.value} className="text-purple-300">
                    {theme.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Games Grid */}
        {filteredGames.length === 0 ? (
          <Card className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 p-12 text-center">
            <BookOpen className="h-16 w-16 text-purple-400/30 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">
              {games.length === 0 
                ? "Aucun jeu dans la bibliothèque" 
                : "Aucun jeu ne correspond à votre recherche"}
            </p>
            {isAdmin() && games.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">Cliquez sur "Ajouter un jeu" pour commencer</p>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGames.map((game) => (
              <Card 
                key={game.game_id}
                onClick={() => setSelectedGame(game)}
                className="bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 overflow-hidden hover:border-purple-400/50 transition-all hover:drop-shadow-[0_0_20px_rgba(147,51,234,0.3)] cursor-pointer"
              >
                {/* Image */}
                <div className="h-48 overflow-hidden bg-purple-900/20">
                  {game.image_url ? (
                    <img 
                      src={getImageUrl(game.image_url)} 
                      alt={game.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-full h-full flex items-center justify-center ${game.image_url ? 'hidden' : ''}`}
                  >
                    <Dices className="h-16 w-16 text-purple-500/30" />
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-purple-300 flex-1">{game.name}</h3>
                  </div>
                  
                  {/* Theme badges */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(game.themes || [game.theme || game.category || 'Autre']).slice(0, 3).map((theme, idx) => (
                      <Badge key={idx} className={`${getThemeColor(theme)} text-white text-xs`}>
                        {theme}
                      </Badge>
                    ))}
                    {(game.themes?.length || 1) > 3 && (
                      <Badge className="bg-gray-600/50 text-gray-300 text-xs">
                        +{game.themes.length - 3}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">{game.description}</p>
                  
                  {/* Click hint */}
                  <p className="text-purple-500/60 text-xs mb-3 italic">Cliquez pour voir la fiche complète</p>
                  
                  {/* Stats */}
                  <div className="flex flex-wrap gap-3 text-sm mb-4">
                    <div className="flex items-center text-purple-400">
                      <Users className="h-4 w-4 mr-1" />
                      <span>{game.min_players}-{game.max_players} joueurs</span>
                    </div>
                    <div className="flex items-center text-purple-400">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{game.duration}</span>
                    </div>
                  </div>
                  
                  {/* Format badges - Only visible to logged-in users */}
                  {user && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(game.format || ['Physique']).map((fmt) => (
                        <Badge 
                          key={fmt} 
                          className={`text-xs ${fmt === 'Numérique' ? 'bg-blue-600/50 border-blue-400/50' : 'bg-green-600/50 border-green-400/50'} text-white`}
                        >
                          {fmt === 'Numérique' ? <Monitor className="h-3 w-3 mr-1" /> : <Package className="h-3 w-3 mr-1" />}
                          {fmt}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Links section */}
                  {(game.digital_link || game.website_url) && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {/* Digital link - Only visible to members */}
                      {isMember() && game.digital_link && (
                        <a 
                          href={game.digital_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <Gamepad2 className="h-3 w-3" />
                          Contenu Numérique
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                      {/* Website - Public */}
                      {game.website_url && (
                        <a 
                          href={game.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                        >
                          <Globe className="h-3 w-3" />
                          Site officiel
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Admin Actions */}
                  {isAdmin() && (
                    <div className="flex gap-2 pt-3 border-t border-purple-500/20">
                      <Button
                        onClick={(e) => { e.stopPropagation(); openEditDialog(game); }}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-purple-500/50 text-purple-400 hover:bg-purple-900/30"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleDeleteGame(game.game_id, game.name); }}
                        variant="outline"
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Game Detail Modal */}
        <Dialog open={!!selectedGame} onOpenChange={(open) => !open && setSelectedGame(null)}>
          <DialogContent className="bg-gradient-to-br from-purple-900/95 to-black border-2 border-purple-500/50 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedGame && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-purple-300 flex items-center gap-3">
                    <Dices className="h-6 w-6" />
                    {selectedGame.name}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Image */}
                  {selectedGame.image_url && (
                    <div className="w-full h-64 rounded-lg overflow-hidden bg-purple-900/20">
                      <img 
                        src={getImageUrl(selectedGame.image_url)} 
                        alt={selectedGame.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Theme and Format Badges */}
                  <div className="flex flex-wrap gap-2">
                    {(selectedGame.themes || [selectedGame.theme || selectedGame.category || 'Autre']).map((theme, idx) => (
                      <Badge key={idx} className={`${getThemeColor(theme)} text-white`}>
                        {theme}
                      </Badge>
                    ))}
                    {user && (selectedGame.format || ['Physique']).map((fmt) => (
                      <Badge 
                        key={fmt} 
                        className={`${fmt === 'Numérique' ? 'bg-blue-600/50 border-blue-400/50' : 'bg-green-600/50 border-green-400/50'} text-white`}
                      >
                        {fmt === 'Numérique' ? <Monitor className="h-3 w-3 mr-1" /> : <Package className="h-3 w-3 mr-1" />}
                        {fmt}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Stats */}
                  <div className="flex flex-wrap gap-6 text-sm">
                    <div className="flex items-center text-purple-400">
                      <Users className="h-5 w-5 mr-2" />
                      <span className="text-white">{selectedGame.min_players}-{selectedGame.max_players} joueurs</span>
                    </div>
                    <div className="flex items-center text-purple-400">
                      <Clock className="h-5 w-5 mr-2" />
                      <span className="text-white">{selectedGame.duration}</span>
                    </div>
                  </div>
                  
                  {/* Full Description */}
                  <div>
                    <h4 className="text-lg font-semibold text-purple-300 mb-2">Description</h4>
                    <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {selectedGame.description}
                    </p>
                  </div>
                  
                  {/* Links section */}
                  {(selectedGame.digital_link || selectedGame.website_url) && (
                    <div className="space-y-3 pt-4 border-t border-purple-500/30">
                      <h4 className="text-lg font-semibold text-purple-300">Liens</h4>
                      <div className="flex flex-wrap gap-4">
                        {/* Digital link - Only visible to members */}
                        {isMember() && selectedGame.digital_link && (
                          <a 
                            href={selectedGame.digital_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600/30 hover:bg-blue-600/50 rounded-lg text-blue-300 hover:text-blue-200 transition-colors"
                          >
                            <Gamepad2 className="h-4 w-4" />
                            Contenu Numérique
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        {/* Website - Public */}
                        {selectedGame.website_url && (
                          <a 
                            href={selectedGame.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600/30 hover:bg-purple-600/50 rounded-lg text-purple-300 hover:text-purple-200 transition-colors"
                          >
                            <Globe className="h-4 w-4" />
                            Site officiel
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Admin Actions in Modal */}
                  {isAdmin() && (
                    <div className="flex gap-3 pt-4 border-t border-purple-500/30">
                      <Button
                        onClick={() => { setSelectedGame(null); openEditDialog(selectedGame); }}
                        className="flex-1 bg-purple-600 hover:bg-purple-500"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Modifier ce jeu
                      </Button>
                      <Button
                        onClick={() => { 
                          const gameToDelete = selectedGame;
                          setSelectedGame(null); 
                          handleDeleteGame(gameToDelete.game_id, gameToDelete.name); 
                        }}
                        variant="outline"
                        className="border-red-500/50 text-red-400 hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default GameLibrary;
